<template>
  <div>
    <vue-element-loading
      :active="appLoading"
      spinner="bar-fade-scale"
      color="#3ea56e"
      size="128"
      is-full-screen
    />
    <v-snackbar v-model="showsnackbar" color="#3ea56e" right>
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">{{ msg }}</v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <v-layout v-if="ServerError" wrap justify-center>
      <v-flex px-5 pt-5 xs12 sm12 md12 lg12 xl12 text-center>
        <ServerError />
      </v-flex>
    </v-layout>

    <div v-else class="add-product-page">
      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-title-group">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon color="#1c1c1c">mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon class="page-title-icon">mdi-store-cog-outline</v-icon>
          <div>
            <h1 class="page-title">Seller Details</h1>
          </div>
        </div>
        <div v-if="user && user._id">
          <v-btn
            depressed dark
            color="#4CAF50"
            class="edit-btn text-capitalize"
            small
            @click="edit(user._id)"
          >
            <v-icon left small>mdi-pencil-outline</v-icon>
            Edit Profile
          </v-btn>
        </div>
      </div>

      <!-- ── Form Body ── -->
      <div class="form-body" v-if="user && seller">
        <!-- ── Hero Profile Card ── -->
        <div class="hero-section mb-5">
          <!-- Cover Image -->
          <div class="hero-cover">
             <v-img
                cover
                :src="coverImageError || !seller.coverimage ? require('@/assets/images/bannernoimage.svg') : mediaURL + seller.coverimage"
                height="220px"
                @error="coverImageError = true"
             >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="#3ea56e"></v-progress-circular>
                  </v-row>
                </template>
             </v-img>
          </div>

          <div class="hero-content with-cover">
            <div class="avatar-container">
              <v-avatar size="100" class="hero-avatar" v-if="user.image">
                <img :src="mediaUURL + user.image" style="object-fit: cover;" />
              </v-avatar>
              <div class="hero-avatar hero-initials" v-else>
                {{ (user.firstname || '').charAt(0).toUpperCase() }}{{ (user.lastname || '').charAt(0).toUpperCase() }}
              </div>
            </div>
            
            <div class="hero-info">
              <div class="d-flex align-center flex-wrap" style="gap: 12px; margin-bottom: 6px;">
                <h2 class="hero-name ma-0">{{ user.firstname }} {{ user.lastname }}</h2>
                <div class="active-chip" :class="user.status === 'Active' || user.status === 'active' ? 'chip-active' : (user.status === 'Pending' ? 'chip-pending' : 'chip-other')">
                  <v-icon size="13" :color="user.status === 'Active' || user.status === 'active' ? '#3ea56e' : (user.status === 'Pending' ? '#f57c00' : '#9e9e9e')">
                    {{ user.status === 'Pending' ? 'mdi-clock-outline' : 'mdi-check-circle' }}
                  </v-icon>
                  <span>{{ user.status || 'Unknown' }}</span>
                </div>
                <v-rating v-if="seller.rating" :value="parseFloat(seller.rating)" background-color="#ffb300" color="#ffb300" dense readonly half-increments size="18"></v-rating>
              </div>
              <div class="hero-email-row mb-1">
                <v-icon size="14" color="#6b7280">mdi-domain</v-icon>
                <span style="font-weight: 500;">{{ user.organization || 'No Organization' }}</span>
              </div>
              <div class="hero-email-row">
                <v-icon size="14" color="#6b7280">mdi-email-outline</v-icon>
                <span>{{ user.email }}</span>
                <v-icon size="14" color="#6b7280" class="ml-3">mdi-phone-outline</v-icon>
                <span>{{ user.phone }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── 01 Profile Details ── -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-number">01</span>
            <div>
              <h2 class="section-title">Profile Information</h2>
            </div>
          </div>
          <div class="section-body">
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">First Name</label>
                  <div class="read-field"><span class="read-value">{{ user.firstname || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Last Name</label>
                  <div class="read-field"><span class="read-value">{{ user.lastname || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Username</label>
                  <div class="read-field"><span class="read-value">{{ user.username || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Account Type</label>
                  <div class="read-field"><span class="read-value">{{ user.accounttype || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Role</label>
                  <div class="read-field"><span class="read-value">{{ user.type || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Commission Percentage</label>
                  <div class="read-field"><span class="read-value">{{ seller.commisionPercentage ? seller.commisionPercentage + '%' : '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Account Created On</label>
                  <div class="read-field"><span class="read-value">{{ user.account_create_at ? formatDate(user.account_create_at) : '—' }}</span></div>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- ── 02 Contact & Address ── -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-number">02</span>
            <div>
              <h2 class="section-title">Contact & Address</h2>
            </div>
          </div>
          <div class="section-body">
            <v-row>
              <v-col cols="12" sm="6" md="4" v-if="user.whatsapp">
                <div class="field-group">
                  <label class="field-label">WhatsApp</label>
                  <div class="read-field"><v-icon size="16" color="#4caf50" class="mr-2">mdi-whatsapp</v-icon><span class="read-value">{{ user.whatsapp }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Street Address</label>
                  <div class="read-field"><span class="read-value">{{ user.streetAddress || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Locality</label>
                  <div class="read-field"><span class="read-value">{{ user.locality || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">City</label>
                  <div class="read-field"><span class="read-value">{{ user.city || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">District</label>
                  <div class="read-field"><span class="read-value">{{ user.district || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Pincode</label>
                  <div class="read-field"><span class="read-value">{{ user.pincode || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Country</label>
                  <div class="read-field"><span class="read-value">{{ user.country || '—' }}</span></div>
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-2" v-if="seller.facebook || seller.instagram">
               <v-col cols="12" sm="6" md="4" v-if="seller.facebook">
                <div class="field-group">
                  <label class="field-label">Facebook URL</label>
                  <div class="read-field"><v-icon size="16" color="#1877f2" class="mr-2">mdi-facebook</v-icon><span class="read-value text-truncate">{{ seller.facebook }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4" v-if="seller.instagram">
                <div class="field-group">
                  <label class="field-label">Instagram URL</label>
                  <div class="read-field"><v-icon size="16" color="#e1306c" class="mr-2">mdi-instagram</v-icon><span class="read-value text-truncate">{{ seller.instagram }}</span></div>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- ── 03 Bank Details ── -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-number">03</span>
            <div>
              <h2 class="section-title">Bank Details</h2>
            </div>
          </div>
          <div class="section-body">
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Account Holder</label>
                  <div class="read-field"><span class="read-value">{{ seller.accountholder || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Account Number</label>
                  <div class="read-field"><span class="read-value">{{ seller.accountnumber || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Bank Name</label>
                  <div class="read-field"><span class="read-value">{{ seller.bankname || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">Branch</label>
                  <div class="read-field"><span class="read-value">{{ seller.branch || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">IFSC Code</label>
                  <div class="read-field"><span class="read-value">{{ seller.ifsccode || '—' }}</span></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="field-group">
                  <label class="field-label">GST ID</label>
                  <div class="read-field"><span class="read-value">{{ seller.gstid || '—' }}</span></div>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- ── Action Row ── -->
        <v-row class="mt-4 pb-12">
          <!-- Pending Seller Actions -->
          <template v-if="user.status === 'Pending'">
             <v-col cols="12" sm="4">
              <v-dialog v-model="dialogApprove" max-width="420px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs" block large depressed color="#3ea56e" dark class="text-capitalize action-btn-large">
                    <v-icon left>mdi-check-circle-outline</v-icon> Approve Seller
                  </v-btn>
                </template>
                <v-card class="pa-2">
                  <v-card-title class="font-weight-bold" style="font-size:16px;">Approve Seller</v-card-title>
                  <v-card-text class="grey--text text--darken-1">Are you sure you want to approve this Seller?</v-card-text>
                  <v-card-actions class="pb-3 px-4">
                    <v-spacer></v-spacer>
                    <v-btn text class="text-capitalize" @click="dialogApprove = false">Cancel</v-btn>
                    <v-btn depressed dark color="#3ea56e" class="text-capitalize" @click="approveSeller()">Approve</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
            <v-col cols="12" sm="4">
              <v-dialog v-model="dialogReject" max-width="420px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs" block large outlined color="#e53935" class="text-capitalize action-btn-large">
                    <v-icon left>mdi-close-circle-outline</v-icon> Reject Seller
                  </v-btn>
                </template>
                <v-card class="pa-2">
                  <v-card-title class="font-weight-bold" style="font-size:16px;">Reject Seller</v-card-title>
                  <v-form v-model="remove" ref="remove">
                    <v-card-text class="pb-0">
                      <p class="grey--text text--darken-1 mb-4">Are you sure you want to reject this Seller? Please provide a reason.</p>
                      <v-text-field
                        outlined dense
                        v-model="reason"
                        label="Reason for rejection"
                        :rules="[v => !!v || 'Reason is required']"
                      ></v-text-field>
                    </v-card-text>
                    <v-card-actions class="pb-3 px-4 pt-0">
                      <v-spacer></v-spacer>
                      <v-btn text class="text-capitalize" @click="dialogReject = false">Cancel</v-btn>
                      <v-btn depressed dark color="#e53935" :disabled="!remove" class="text-capitalize" @click="rejectSeller()">Reject</v-btn>
                    </v-card-actions>
                  </v-form>
                </v-card>
              </v-dialog>
            </v-col>
          </template>

          <!-- Normal Seller Actions (Delete) -->
          <v-col cols="12" sm="4" v-else>
             <v-dialog v-model="dialog" max-width="420px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs" block large outlined color="#e53935" class="text-capitalize action-btn-large">
                    <v-icon left>mdi-delete-outline</v-icon> Remove Seller
                  </v-btn>
                </template>
                <v-card class="pa-2">
                  <v-card-title class="font-weight-bold" style="font-size:16px;">Confirm Removal</v-card-title>
                  <v-card-text class="grey--text text--darken-1">Are you sure you want to remove this Seller? This action cannot be undone.</v-card-text>
                  <v-card-actions class="pb-3 px-4">
                    <v-spacer></v-spacer>
                    <v-btn text class="text-capitalize" @click="dialog = false">Cancel</v-btn>
                    <v-btn depressed dark color="#e53935" class="text-capitalize" @click="deleteuser()">Delete</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
          </v-col>

        </v-row>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
// import ProductDetails from "./productDetails"
export default {
  // components: { SellerRating,ProductDetails },
  props: ["sellerid"],
  data() {
    return {
      showsnackbar: false,
      ServerError: false,
      user: null,
      photos: [],
      seller: null,
      subcat: [],
      productStatus: false,
      product: [],
      productArray: [],
      categoryArray: [],
      msg: null,
      widthOfCard: "250px",
      appLoading: false,
      coverImageError: false,
      dialog: false,
      userStatus: null,
      userRole: localStorage.getItem("role"),
      tab: null,
      text: "",
      reason: "",
      remove: false,
      reviewDialog: false,
      dialogApprove: false,
      dialogReject:false,
      navItems: [
        { name: "Products", type: null },
        { name: "Offers", type: "Offer" },
        { name: "Deals", type: "Deals" },
      ],
      items: [" Profile Details", "  Bank Details"],
      // " Product Details"
      currentType: null,
    };
  },
  mounted() {
    this.getData();
    // this.productInfo();
  },
  methods: {
    winStepper(window_data) {
      if (window_data.type == "rating") {
        if (window_data.getData) this.getData();
      }
      if (window_data.showsnackbar) {
        this.showsnackbar = true;
        this.msg = window_data.msg;
      }
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/seller/info",
        params: {
          id: this.$route.query.seller,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.user = response.data.data;
            this.seller = response.data.data.seller;
            // this.userStatus = this.user.status;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    deleteuser() {
      var data = {};
      data["id"] = this.$route.query.seller;
      axios({
        url: "/user/remove",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.dialog = false;
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Deleted Sucessfully";
            this.showsnackbar = true;
            this.$router.go(-1);
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    approveSeller() {
      axios({
        method: "POST",
        url: "seller/approve/",
        data: {
          id: this.$route.query.seller,
          approve: true,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.dialogApprove = false;
          this.appLoading = false;
          if (response.data.status) {
            this.msg = "Approved";
            this.showsnackbar = true;
            this.$router.go(-1);
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    rejectSeller() {
      axios({
        method: "POST",
        url: "seller/approve/",
        data: {
          id: this.$route.query.seller,
          approve: false,
          reason: this.reason,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.dialogReject = false;
          this.appLoading = false;
          if (response.data.status) {
            // this.getData();
            this.$router.go(-1);
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    productinfo(pid) {
      this.$router.push("/sellerProducts/products/" + pid);
    },
    edit(sid) {
      this.$router.push("/editSeller?seller=" + sid);
    },
    formatDate(item) {
      var dt = new Date(item);
      var day = dt.getDate();
      var year = dt.getFullYear();
      var hours = dt.getHours();
      var minutes = dt.getMinutes();
      dt = dt.toString();
      var ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime =
        day + " " + dt.slice(4, 7) + " " + year + " " + hours + ":" + minutes + " " + ampm;
      return strTime;
    },
  },
};
</script>

<style scoped>
/* ── Shared Styles ── */
.add-product-page {
  min-height: 100vh;
  background: #f6f8fb;
}

/* Page Header */
.page-header {
  background: #ffffff;
  border-bottom: 1px solid #e8ecf0;
  padding: 20px 28px;
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.page-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}
.page-title-icon {
  font-size: 28px !important;
  color: #4caf50;
}
.page-title {
  font-family: poppinssemibold, sans-serif;
  font-size: 20px;
  color: #1c1c1c;
  margin: 0;
  line-height: 1.2;
}
.edit-btn {
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  box-shadow: 0 3px 10px rgba(76,175,80,0.25) !important;
}

/* Form Body */
.form-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

/* ── Hero Section ── */
.hero-section {
  border-radius: 12px;
  border: 1px solid #e8ecf0;
  overflow: hidden;
  position: relative;
  background: #fff;
}
.hero-cover {
  width: 100%;
  height: 220px;
  background: #e8f5e9;
  position: relative;
}
.hero-content.with-cover {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: 24px;
  padding: 0 28px 24px;
  margin-top: -50px;
}
.avatar-container {
  position: relative;
  flex-shrink: 0;
}
.hero-avatar {
  border: 4px solid #fff !important;
  box-shadow: 0 4px 14px rgba(0,0,0,0.1) !important;
  background: #fff;
}
.hero-initials {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #c8e6c9;
  color: #2d8257;
  font-size: 32px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-info {
  flex: 1;
  padding-top: 55px; /* push down below the avatar top line */
}
.hero-name {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a2e;
}
.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
}
.chip-active { background: #e8f5e9; color: #2d8257; border: 1px solid #c8e6c9; }
.chip-pending { background: #fff3e0; color: #e65100; border: 1px solid #ffe0b2; }
.chip-other { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }

.hero-email-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
}

/* ── Section Cards ── */
.form-section {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e8ecf0;
  margin-bottom: 20px;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}
.form-section:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}
.section-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #f0f3f7;
  background: #fafbfc;
}
.section-number {
  font-family: poppinssemibold, sans-serif;
  font-size: 22px;
  color: #d0d8e4;
  line-height: 1;
  min-width: 36px;
}
.section-title {
  font-family: poppinssemibold, sans-serif;
  font-size: 15px;
  color: #1c1c1c;
  margin: 0;
}
.section-body { padding: 20px 24px; }

/* Field Groups */
.field-group { margin-bottom: 16px; }
.field-label {
  display: block;
  font-family: poppinssemibold, sans-serif;
  font-size: 12px;
  color: #555;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.read-field {
  display: flex;
  align-items: center;
  min-height: 40px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 0 12px;
  background: #fdfdfd;
}
.read-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
}
.action-btn-large {
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
}

@media (max-width: 960px) {
  .hero-content.with-cover {
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-top: -60px;
  }
  .hero-info { padding-top: 10px; }
  .hero-email-row { justify-content: center; }
  .d-flex.align-center { justify-content: center; }
}
</style>
